import { createAsyncThunk } from '@reduxjs/toolkit';
import i18n from '../../i18n/i18n';
import {
  CreateSyllabusPayload,
  IDeleteSyllabusResponse,
  ISyllabusByIdResponse,
  IUpdateSyllabusPayload,
  IUpdateSyllabusStatusPayload,
  IUpdateSyllabusStatusResponse,
  SyllabusCreateUpdateResponse,
  SyllabusPathParams,
  SyllabusResponse,
} from '../../interface/syllabus.interface';
import {
  getSyllabus,
  createSyllabus,
  modifySyllabus,
  getSyllabusById,
  deleteSyllabus,
  updateSyllabusStatus,
} from '../../services/syllabus/syllabus';
import { IErrorResponse } from '../../interface/main.interface';

export const fetchSyllabus = createAsyncThunk<
  SyllabusResponse,
  SyllabusPathParams | undefined,
  { rejectValue: IErrorResponse }
>('syllabus/fetch', async (params, { rejectWithValue }) => {
  try {
    const response = await getSyllabus(params || {});
    return response;
  } catch (err: any) {
    return rejectWithValue({
      status: err?.status || 500,
      message: err?.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

export const fetchSyllabusById = createAsyncThunk<
  ISyllabusByIdResponse,
  string,
  { rejectValue: IErrorResponse }
>('syllabus/fetch-by-id', async (id, { rejectWithValue }) => {
  try {
    const response = await getSyllabusById(id);
    return response;
  } catch (err: any) {
    return rejectWithValue({
      status: err?.status || 500,
      message: err?.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

export const addSyllabus = createAsyncThunk<
  SyllabusCreateUpdateResponse,
  CreateSyllabusPayload,
  { rejectValue: IErrorResponse }
>('syllabus/create', async (payload, { rejectWithValue }) => {
  try {
    const response = await createSyllabus(payload);
    return response;
  } catch (err: any) {
    return rejectWithValue({
      status: err?.status || 500,
      message: err?.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

export const updateSyllabus = createAsyncThunk<
  SyllabusCreateUpdateResponse,
  IUpdateSyllabusPayload,
  { rejectValue: IErrorResponse }
>('syllabus/update', async (payload, { rejectWithValue }) => {
  try {
    const response = await modifySyllabus(payload);
    return response;
  } catch (err: any) {
    return rejectWithValue({
      status: err?.status || 500,
      message: err?.message || i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

/**
 * Permanently delete a syllabus group.
 * The axios interceptor rejects with the backend body, so on failure we
 * preserve the machine-readable `errorCode` for the UI (mirrors the subject
 * delete thunk). Syllabus delete has no soft/hard gate — the only error code is
 * NOT_FOUND.
 */
export const deleteSyllabusThunk = createAsyncThunk<
  IDeleteSyllabusResponse,
  string,
  { rejectValue: IErrorResponse & { errorCode?: string } }
>('syllabus/delete', async (id, { rejectWithValue }) => {
  try {
    const response = await deleteSyllabus(id);
    return response;
  } catch (err: any) {
    return rejectWithValue({
      status: err?.status || 500,
      message: err?.message || 'Failed to delete syllabus.',
      errorCode: err?.errorCode,
    });
  }
});

/**
 * Toggle a syllabus group's active status.
 * The axios interceptor rejects with the backend body, so on failure we read
 * `message`/`errorCode` directly off the error (mirrors deleteSyllabusThunk).
 * Toggling is unconditional and reversible — the only error code is NOT_FOUND.
 */
export const updateSyllabusStatusThunk = createAsyncThunk<
  IUpdateSyllabusStatusResponse,
  IUpdateSyllabusStatusPayload,
  { rejectValue: IErrorResponse & { errorCode?: string } }
>('syllabus/update-status', async (payload, { rejectWithValue }) => {
  try {
    const response = await updateSyllabusStatus(payload);
    return response;
  } catch (err: any) {
    return rejectWithValue({
      status: err?.status || 500,
      message: err?.message || 'Failed to update syllabus status.',
      errorCode: err?.errorCode,
    });
  }
});

export const syllabusActions = {
  fetchSyllabus,
  fetchSyllabusById,
  addSyllabus,
  updateSyllabus,
  deleteSyllabus: deleteSyllabusThunk,
  updateSyllabusStatus: updateSyllabusStatusThunk,
};
